import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import container from 'src/container';
import { getPrimaryRole, UserRole } from 'src/domain/user';
import {
    registerWebPushToken,
    subscribeToForegroundWebPushMessages,
    startForegroundWebPushListener,
} from 'src/services/webPush.service';
import { useSignedInUser } from './useSignedInUser';

const {
    cradle: { munduClient },
} = container;

const canUseWebPush = (role?: UserRole) => {
    return (
        role === UserRole.creator ||
        role === UserRole.organization ||
        role === UserRole.organizationB2C
    );
};

export type WebPushPermissionStatus = NotificationPermission | 'unsupported';

const getNotificationPermissionStatus = (): WebPushPermissionStatus => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
        return 'unsupported';
    }

    return Notification.permission;
};

export const useWebPushRegistration = () => {
    const { i18n } = useTranslation();
    const user = useSignedInUser();
    const userId = user?.id;
    const role = getPrimaryRole(user);
    const { mutate } = useSWRConfig();
    const stopForegroundListenerRef = useRef<(() => void) | undefined>();
    const [permissionStatus, setPermissionStatus] =
        useState<WebPushPermissionStatus>(getNotificationPermissionStatus);
    const [isRegistering, setIsRegistering] = useState(false);
    const isEligible = Boolean(
        userId && canUseWebPush(role) && !user?.supportMode,
    );
    const locale = i18n.language.startsWith('en') ? 'en' : 'nb';

    const syncWebPushToken = useCallback(
        async (requestPermission = false) => {
            if (!isEligible) {
                return;
            }

            setIsRegistering(true);

            try {
                const token = await registerWebPushToken({
                    requestPermission,
                    onPermissionChange: setPermissionStatus,
                });
                setPermissionStatus(getNotificationPermissionStatus());

                if (!token) {
                    return;
                }

                await munduClient.registerPushToken(
                    token,
                    'web',
                    'web',
                    locale,
                );
                stopForegroundListenerRef.current =
                    await startForegroundWebPushListener();
            } catch (error) {
                console.warn('Failed to register web push token', error);
                setPermissionStatus(getNotificationPermissionStatus());
            } finally {
                setIsRegistering(false);
            }
        },
        [isEligible, locale],
    );

    useEffect(() => {
        setPermissionStatus(getNotificationPermissionStatus());

        if (!isEligible) {
            return;
        }

        const stopForegroundUpdates = subscribeToForegroundWebPushMessages(
            payload => {
                const type = payload.type;

                if (type === 'message' || type === 'message_comment') {
                    void mutate(
                        (key: unknown) =>
                            Array.isArray(key) && key[0] === 'messages',
                        undefined,
                        { revalidate: true },
                    );
                }

                if (type === 'message_comment') {
                    const targetMessageId = payload.messageId;

                    void mutate(
                        (key: unknown) =>
                            Array.isArray(key) &&
                            key[0] === 'messageComments' &&
                            (!targetMessageId || key[1] === targetMessageId),
                        undefined,
                        { revalidate: true },
                    );
                }

                if (type === 'event' || type === 'event_reminder') {
                    void mutate(
                        (key: unknown) =>
                            Array.isArray(key) && key[0] === 'events',
                        undefined,
                        { revalidate: true },
                    );
                }
            },
        );

        void syncWebPushToken(false);

        return () => {
            stopForegroundListenerRef.current?.();
            stopForegroundUpdates();
        };
    }, [isEligible, mutate, syncWebPushToken]);

    return {
        isEligible,
        isRegistering,
        permissionStatus,
        requestWebPushPermission: () => syncWebPushToken(true),
    };
};
